import { FunctionalComponent, h, Build } from '@stencil/core';
import { host } from '../shared/config'
import get from 'lodash/get'

interface Thumbnail {
    name: string;
    hash: string;
    ext: string;
    mime: string;
    width: number;
    height: number;
    size: number;
    path?: any;
    url: string;
}

interface Large {
    name: string;
    hash: string;
    ext: string;
    mime: string;
    width: number;
    height: number;
    size: number;
    path?: any;
    url: string;
}

interface Medium {
    name: string;
    hash: string;
    ext: string;
    mime: string;
    width: number;
    height: number;
    size: number;
    path?: any;
    url: string;
}

interface Small {
    name: string;
    hash: string;
    ext: string;
    mime: string;
    width: number;
    height: number;
    size: number;
    path?: any;
    url: string;
}

interface Formats {
    thumbnail: Thumbnail;
    large: Large;
    medium: Medium;
    small: Small;
}

interface Media {
    id: number;
    name: string;
    alternativeText: string;
    caption: string;
    width: number;
    height: number;
    formats: Formats;
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: any;
    provider: string;
    provider_metadata?: any;
    created_at: Date;
    updated_at: Date;
    focalPoint?: {
        x: number;
        y: number;
    };
}

interface iMedia {
    media: Media
    height?: string
    width?: string
    alt?: string
    className?: string
    cover?:boolean
    autoplay?:string
    size?:string
    eager?: boolean
    hostOverride?: string
}

const getFocalPointStyle = (data: any, cover = false) => {
    const x = Number(get(data, 'focalPoint.x'));
    const y = Number(get(data, 'focalPoint.y'));

    if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;

    const focalX = Math.min(100, Math.max(0, x));
    const focalY = Math.min(100, Math.max(0, y));
    const style = {
        objectPosition: `${focalX}% ${focalY}%`,
    };

    if (!cover) return style;

    // UIkit's cover mode sizes and absolutely positions the image instead of
    // using object-fit, so mirror object-position through its positioning vars.
    return {
        ...style,
        left: `${focalX}%`,
        top: `${focalY}%`,
        '--uk-position-translate-x': `-${focalX}%`,
        '--uk-position-translate-y': `-${focalY}%`,
    };
};

export const AppMedia: FunctionalComponent<iMedia> = ({ media, height, width, alt, className, cover, autoplay, size, eager, hostOverride }) => {

    if (!Build.isBrowser && !eager) return;

    if (!media) return;
    const data = get(media, 'data.attributes') || get(media, 'attributes')
    if(!data) return;
    let { mime, alternativeText } = data
    const isCover = cover ? {'uk-cover':true} : {};

    let url, srcset, sizes;
    url = get(data, 'url');

    const imageHost = hostOverride || host;

    if (['image/png', 'image/jpeg', 'image.webp'].includes(mime)){
        // uikit responsive images
        const urlOptions = [];
        if (get(data, 'formats.small.url')) urlOptions.push(`${imageHost}${data.formats.small.url} ${data.formats.small.width}w`)
        if (get(data, 'formats.medium.url')) urlOptions.push(`${imageHost}${data.formats.medium.url} ${data.formats.medium.width}w`)
        if (get(data, 'formats.large.url')) urlOptions.push(`${imageHost}${data.formats.large.url} ${data.formats.large.width}w`)
        if (get(data, 'url')) urlOptions.push(`${imageHost}${data.url} ${data.width}w`)
        srcset = urlOptions.join(', ')
        sizes = size || "(max-width: 638px) 90vh, (max-width: 958px) 100vw, 100vw"
    }

    if (!mime || !url) return;
    const attrs = {}
    if (autoplay) attrs['autoplay'] = true;

    // Don't render local videos on SSG
    if(!Build.isBrowser && mime=='video/mp4') mime = '';

    const newHeight = height || get(data, 'height');
    const newWidth = width || get(data, 'width');
    const focalPointStyle = getFocalPointStyle(data, cover);

    switch (mime) {
        case 'video/mp4': return <video height={height} width={width} class={className} src={`${imageHost}${url}`} autoplay={autoplay} muted playsinline controls={false} loop preload='metadata' {...attrs} {...isCover}></video>
        case 'image/webp': return <img height={newHeight} width={newWidth} class={className} style={focalPointStyle} alt={alt || alternativeText} src={`${imageHost}${url}`} srcset={srcset} sizes={sizes} loading={eager ? 'eager' : 'lazy'} {...isCover}></img>
        case 'image/png': return <img height={newHeight} width={newWidth} class={className} style={focalPointStyle} alt={alt || alternativeText} src={`${imageHost}${url}`} srcset={srcset} sizes={sizes} loading={eager ? 'eager' : 'lazy'} {...isCover}></img>
        case 'image/jpeg': return <img height={newHeight} width={newWidth} class={className} style={focalPointStyle} alt={alt || alternativeText} src={`${imageHost}${url}`} srcset={srcset} sizes={sizes} loading={eager ? 'eager' : 'lazy'} {...isCover}></img>
        case 'image/svg+xml': return <img height={height} width={width} class={`uk-preserve ${className}`} src={`${imageHost}${url}`} aria-label={alt} uk-svg {...isCover} />
    }
};

export { getFocalPointStyle };
